import { Component, computed, DestroyRef, effect, inject, viewChild, viewChildren } from '@angular/core';
import { RouterLink } from '@angular/router';
import { uniq } from 'lodash-es';
import { Subject } from 'rxjs';
import { CommentReplyComponent } from '../../../component/comment/comment-reply/comment-reply.component';
import { CommentThreadComponent } from '../../../component/comment/comment-thread/comment-thread.component';
import { ThreadSummaryComponent } from '../../../component/comment/thread-summary/thread-summary.component';
import { LoadingComponent } from '../../../component/loading/loading.component';
import { RefListComponent } from '../../../component/ref/ref-list/ref-list.component';
import { HasChanges } from '../../../guard/pending-changes.guard';

import { Ref } from '../../../model/ref';
import { getMailbox, mailboxes } from '../../../mods/mailbox';
import { AdminService } from '../../../service/admin.service';
import { RefService } from '../../../service/api/ref.service';
import { ModService } from '../../../service/mod.service';
import { QueryStore } from '../../../store/query';
import { Store } from '../../../store/store';
import { ThreadStore } from '../../../store/thread';
import { getTitle } from '../../../util/format';
import { getArgs } from '../../../util/query';
import { hasTag, pluginResponses, removeTag, top, updateMetadata } from '../../../util/tag';

@Component({
  selector: 'app-ref-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  imports: [CommentReplyComponent, RouterLink, ThreadSummaryComponent, RefListComponent, LoadingComponent]
})
export class RefSummaryComponent implements HasChanges {
  private mod = inject(ModService);
  admin = inject(AdminService);
  refs = inject(RefService);
  store = inject(Store);
  thread = inject(ThreadStore);
  query = inject(QueryStore);


  newResp$ = new Subject<Ref | undefined>();
  newComment$ = new Subject<Ref | undefined>();
  newThread$ = new Subject<Ref | undefined>();

  summaryItems = 5;

  readonly reply = viewChild<CommentReplyComponent>('reply');
  readonly threadComponents = viewChildren(CommentThreadComponent);
  readonly list = viewChild<RefListComponent>('list');

  constructor() {
    const store = this.store;
    store.view.defaultSort.set(['published,DESC']);
    // TODO: set title for bare reposts
    effect(() => this.mod.setTitle(getTitle(this.store.view.ref())));
    this.thread.watch(() => ({
      top: this.store.view.url(),
      sort: this.store.view.sort(),
      filters: this.store.view.filter(),
      search: this.store.view.search(),
    }));
    this.query.watch(() => ({
      ...getArgs(
        '',
        this.store.view.sort(),
        uniq(['query/!internal', 'query/!plugin/comment', 'query/!plugin/thread', ...this.store.view.filter()]),
        this.store.view.search(),
        this.store.view.pageNumber(),
        this.summaryItems,
      ),
      responses: this.store.view.url(),
    }));
  }

  saveChanges() {
    const reply = this.reply();
    const list = this.list();
    return (!reply || reply.saveChanges())
      && (!list || list.saveChanges())
      && !this.threadComponents()?.find(t => !t.saveChanges());
  }

  private readonly destroyCleanup = inject(DestroyRef).onDestroy(() => {
    this.newResp$.complete();
    this.newComment$.complete();
    this.newThread$.complete();
  });

  readonly top = computed(() => top(this.store.view.ref()));

  readonly responseSet = computed(() => this.comments() || this.threads() || this.admin.responseButton().find(p => hasTag(p.tag, this.replyTags())));

  readonly dm = computed(() => !!this.admin.getTemplate('dm') && hasTag('dm', this.store.view.ref()));

  readonly comments = computed(() => {
    if (!this.admin.getPlugin('plugin/comment')) return 0;
    return pluginResponses(this.store.view.ref(), 'plugin/comment');
  });

  readonly threads = computed(() => {
    if (!this.admin.getPlugin('plugin/thread')) return 0;
    return pluginResponses(this.store.view.ref(), 'plugin/thread');
  });

  readonly responses = computed(() => this.store.view.ref()?.metadata?.responses || 0);

  readonly mailboxes = computed(() => mailboxes(this.store.view.ref()!, this.store.account.tag(), this.store.origins.originMap()));

  readonly replyTags = computed((): string[] => {
    const tags = [
      ...this.comments() ? ['plugin/comment'] : this.threads() ? ['plugin/thread'] : [],
      ...this.admin.reply().filter(p => hasTag(p.tag, this.store.view.ref())).flatMap(p => p.config!.reply as string[]),
      ...this.mailboxes(),
    ];
    return removeTag(getMailbox(this.store.account.tag(), this.store.account.origin()), uniq(tags));
  });

  readonly moreComments = computed(() => {
    const topComments = this.thread.cache().get(this.top());
    if (!topComments) return false;
    return topComments.length > this.summaryItems;
  });

  onReply(ref?: Ref) {
    if (ref && this.store.view.ref()) {
      updateMetadata(this.store.view.ref()!, ref);
    }
    this.store.eventBus.reload(ref);
    if (hasTag('plugin/comment', ref)) {
      this.newComment$?.next(ref);
    } else if (hasTag('plugin/thread', ref)) {
      this.newThread$?.next(ref);
    } else if (!hasTag('internal', ref)) {
      this.newResp$?.next(ref);
    }

  }
}
